import { NotificationStack } from '@studio/design-system/notification';
import T from '@studio/design-system/themes';
import type { T_Nullable } from '@tryiris-ai/js-types';
import { toObjectValues } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { Fragment, useMemo } from 'react';
import { twMerge } from 'cn';
// eslint-disable-next-line import/no-internal-modules -- app-specific toast content stays outside design-system.
import { AgentRestoreFailureNotice } from '../../../components/agent-restore-failure-notice.jsx';
import { Notice } from '../notice/index.js';
import type { Props } from './types.js';
import { useDepot } from '#~/depot/hooks.js';
import { useNotificationDevicePane } from '../../device-pane.js';
import { useNotificationStackHidden } from '../../stack-visibility.js';
import { close, markRead } from '#~/notifications/index.js';
import { PanelClassName } from './class-name.js';
import { useRecordingSavedNotices } from '../../hooks/use-recording-saved-notices.js';

// This leaf survives route changes without rerendering the stack on camera ticks.
const RecordingSavedObserver = ({ accountId }: { accountId: string }): null => {
  useRecordingSavedNotices(accountId);
  return null;
};

/**
 * Notification `<Panel>` component.
 *
 * @see https://bit.ly/3reQ2xL
 */
export const Panel = ({ className, ...props }: Props): T_Nullable<ReactElement> => {
  const accountId = useDepot((state) => state.account?.id);
  const devicePane = useNotificationDevicePane();
  const isHidden = useNotificationStackHidden();
  const notifications = useDepot((µ) => µ.notifications);

  const visibleNotifications = useMemo(() => {
    const notificationEntries = toObjectValues(notifications);
    const visibleEntries: typeof notificationEntries = [];

    for (const entry of notificationEntries) {
      // eslint-disable-next-line unicorn/no-unreadable-array-destructuring
      const [, , { isClosed }] = entry;

      if (!isClosed) {
        visibleEntries.push(entry);
      }
    }

    // Keep newest-first order within each group; opening a pane never mutates notification history.
    visibleEntries.sort(([a], [b]) => {
      const priorityA = devicePane !== undefined && a.deviceId === devicePane;
      const priorityB = devicePane !== undefined && b.deviceId === devicePane;
      return Number(priorityB) - Number(priorityA) || b.timestamp.getTime() - a.timestamp.getTime();
    });

    return visibleEntries;
  }, [notifications, devicePane]);

  return (
    <div className={twMerge(PanelClassName, className)} data-theme={T.getTheme('light')} {...props}>
      {accountId && <RecordingSavedObserver key={accountId} accountId={accountId} />}
      {/* Per rule: keyboard-reachable-interactive-elements (ai.knowledge/global) — tall stacks stay scrollable. */}
      <div className='flex h-full items-end justify-end overflow-y-auto overflow-x-hidden py-24'>
        {/* Keep the restore notice mounted: its dismissal lives in component
            state. Depot notices unmount inside stable keyed slots to restart
            their timers, without AnimatePresence retaining a running timer. */}
        <NotificationStack hidden={isHidden} className='mt-auto' itemClassName='pr-24'>
          {visibleNotifications.map(([notification, options]) => {
            const { onClose, ...rest } = options ?? {};

            return (
              <Fragment key={notification.id}>
                {!isHidden && (
                  <Notice
                    notification={notification}
                    // The panel always closes the notice in the store; a caller's
                    // `onClose` runs beside it, never instead (ENG-4930 item 2).
                    onClose={(id, reason): void => {
                      close(id, reason);
                      onClose?.(id, reason);
                    }}
                    onMouseOver={(): void => {
                      markRead(notification.id);
                    }}
                    {...rest}
                  />
                )}
              </Fragment>
            );
          })}
          <AgentRestoreFailureNotice key='agent-restore-failure-notice' />
        </NotificationStack>
      </div>
    </div>
  );
};
